// sections/@dashboard/finance/FinanceDashboard.tsx
import BusinessInsights from './BusinessInsights';
import FinancialTrendComparison from './FinancialTrendComparison/FinancialTrendComparison';
import BusinessHealthSummary from './BusinessHealthSummary/BusinessHealthSummary';
import FinanceCharts from './FinanceCharts';
import DashboardSummary from './DashboardSummary';
import { useMemo } from 'react';
import {
  Grid,
  Card,
  Box,
  Typography,
  Stack,
  LinearProgress,
  Button,
  Chip,
  Divider,
  Skeleton,
} from '@mui/material';
import Iconify from '../../../components/iconify';
import { fCurrency } from '../../../utils/formatNumber';
import {
  IFinanceStats,
  ITransactionCategory,
  ICategorySummary,
  IVatSummary,
} from '../../../@types/finance';
import { formatLabel } from '../../../utils/formatLabel';

// ── Category type colours ─────────────────────────────────────────────────────
const TYPE_COLOR = {
  income: {
    bg: 'success.lighter',
    text: 'success.dark',
    border: 'success.light',
  },
  expenditure: {
    bg: 'error.lighter',
    text: 'error.dark',
    border: 'error.light',
  },
  finance: {
    bg: 'secondary.lighter',
    text: 'secondary.dark',
    border: 'secondary.light',
  },
};

// VAT code badge colours
const VAT_STYLE: Record<string, { bg: string; color: string }> = {
  S: { bg: '#dbeafe', color: '#1d4ed8' },
  E: { bg: '#dcfce7', color: '#15803d' },
  Z: { bg: '#f3f4f6', color: '#374151' },
  N: { bg: '#fef9c3', color: '#854d0e' },
};

// ── Top-level KPI card ────────────────────────────────────────────────────────
function KpiCard({
  label,
  value,
  sub,
  icon,
  color,
  onClick,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: string;
  color: 'success' | 'error' | 'warning' | 'info' | 'secondary';
  onClick?: () => void;
}) {
  return (
    <Card
      onClick={onClick}
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s',
        '&:hover': onClick ? { boxShadow: 3 } : {},
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.5,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: `${color}.lighter`,
          color: `${color}.main`,
        }}
      >
        <Iconify icon={icon} width={20} />
      </Box>
      <Stack spacing={0.25} sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          title={label}
        >
          {label}
        </Typography>
        <Typography
          fontFamily="monospace"
          fontWeight={700}
          color={`${color}.dark`}
          title={value}
          sx={{
            fontSize: 'clamp(14px, 1.6vw, 20px)',
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </Typography>
        {sub && (
          <Typography
            variant="caption"
            color="text.disabled"
            noWrap
            title={sub}
          >
            {sub}
          </Typography>
        )}
      </Stack>
    </Card>
  );
}

// ── Category summary tile ─────────────────────────────────────────────────────
function CategoryTile({ summary }: { summary: ICategorySummary }) {
  const colors = TYPE_COLOR[summary.categoryType] ?? TYPE_COLOR.finance;
  const vatStyle = summary.vatCode ? VAT_STYLE[summary.vatCode] : null;

  return (
    <Card
      variant="outlined"
      sx={{
        p: 1.75,
        borderRadius: 1.5,
        borderColor: colors.border,
        '&:hover': { borderColor: colors.text, boxShadow: 1 },
        transition: 'all 0.15s',
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        mb={1}
      >
        <Typography
          variant="caption"
          fontWeight={600}
          color={colors.text}
          sx={{ lineHeight: 1.3, flex: 1, pr: 0.5 }}
        >
          {summary.categoryName}
        </Typography>
        {vatStyle && summary.vatCode && (
          <Typography
            variant="caption"
            sx={{
              px: 0.75,
              py: 0.1,
              borderRadius: 0.5,
              bgcolor: vatStyle.bg,
              color: vatStyle.color,
              fontWeight: 700,
              fontFamily: 'monospace',
              fontSize: 10,
              flexShrink: 0,
            }}
          >
            {summary.vatCode}
          </Typography>
        )}
      </Stack>

      <Typography
        variant="h6"
        fontFamily="monospace"
        color={colors.text}
        noWrap
        title={fCurrency(summary.totalAmount)}
        sx={{
          fontSize: 15,
          fontWeight: 700,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {fCurrency(summary.totalAmount)}
      </Typography>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mt={0.75}
      >
        <Typography variant="caption" color="text.disabled">
          {summary.transactionCount} transaction
          {summary.transactionCount !== 1 ? 's' : ''}
        </Typography>
        <Chip
          label={formatLabel(summary.categoryType)}
          size="small"
          sx={{
            height: 16,
            fontSize: 9,
            fontWeight: 600,
            bgcolor: colors.bg,
            color: colors.text,
            textTransform: 'capitalize',
          }}
        />
      </Stack>
    </Card>
  );
}

// ── Reconciliation progress bar ───────────────────────────────────────────────
function ReconciliationBar({
  stats,
  onGo,
}: {
  stats: IFinanceStats;
  onGo: () => void;
}) {
  const total = stats.totalTransactions;
  const pct = total > 0 ? Math.round((stats.reconciledCount / total) * 100) : 0;

  return (
    <Card sx={{ p: 2.5 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={1.5}
      >
        <Stack spacing={0.25}>
          <Typography variant="subtitle2">Reconciliation status</Typography>
          <Typography variant="caption" color="text.secondary">
            {stats.reconciledCount} reconciled · {stats.unmatchedCount}{' '}
            unmatched · {stats.pendingCount} pending
          </Typography>
        </Stack>
        <Button
          size="small"
          onClick={onGo}
          endIcon={<Iconify icon="eva:arrow-forward-fill" width={14} />}
        >
          Reconcile
        </Button>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={pct >= 90 ? 'success' : pct >= 60 ? 'warning' : 'error'}
        sx={{ height: 8, borderRadius: 1, mb: 1 }}
      />
      <Stack direction="row" spacing={2.5}>
        {[
          {
            label: 'Reconciled',
            count: stats.reconciledCount,
            color: 'success.main',
          },
          {
            label: 'Unmatched',
            count: stats.unmatchedCount,
            color: 'error.main',
          },
          {
            label: 'Pending',
            count: stats.pendingCount,
            color: 'warning.main',
          },
        ].map((item) => (
          <Stack
            key={item.label}
            direction="row"
            alignItems="center"
            spacing={0.75}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: item.color,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {item.label}: <strong>{item.count}</strong>
            </Typography>
          </Stack>
        ))}
        <Box sx={{ flexGrow: 1 }} />
        <Typography
          variant="caption"
          fontFamily="monospace"
          color="text.secondary"
        >
          {pct}% complete
        </Typography>
      </Stack>
    </Card>
  );
}

// ── VAT snapshot ──────────────────────────────────────────────────────────────
// Reads the same quarter-based VAT summary as the "VAT & Partial Exemption" tab
// (via dateToQuarter) so the two never disagree for the same period; falls
// back to the period-wide finance stats only until that summary has loaded.
function VatSnapshot({
  stats,
  vatSummary,
}: {
  stats: IFinanceStats;
  vatSummary: IVatSummary | null;
}) {
  const peRate = vatSummary ? vatSummary.peRecoveryRate : stats.vatPeRate;
  const vatRecoverable = vatSummary
    ? vatSummary.recoverableVat
    : stats.vatRecoverable;
  const pePercent = (peRate * 100).toFixed(1);
  return (
    <Card sx={{ p: 2.5, height: '100%' }}>
      <Typography variant="subtitle2" mb={1.5}>
        VAT & partial exemption snapshot
      </Typography>
      <Stack spacing={1}>
        {[
          { label: 'PE recovery rate', value: `${pePercent}%`, mono: true },
          {
            label: 'VAT recoverable',
            value: fCurrency(vatRecoverable),
            mono: true,
          },
        ].map((row) => (
          <Stack
            key={row.label}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
          >
            <Typography variant="caption" color="text.secondary" noWrap>
              {row.label}
            </Typography>
            <Typography
              variant="caption"
              fontWeight={600}
              fontFamily={row.mono ? 'monospace' : undefined}
              noWrap
              title={row.value}
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {row.value}
            </Typography>
          </Stack>
        ))}
        <Divider />
        <LinearProgress
          variant="determinate"
          value={parseFloat(pePercent)}
          color="secondary"
          sx={{ height: 6, borderRadius: 1 }}
        />
        <Typography variant="caption" color="text.disabled">
          {pePercent}% of overhead VAT is recoverable
        </Typography>
      </Stack>
    </Card>
  );
}

// ── Skeleton loaders ──────────────────────────────────────────────────────────
function DashboardSkeleton() {
  return (
    <Stack spacing={2.5}>
      <Grid container spacing={2}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Grid item xs={12} sm={6} md={4} lg={4} xl={2} key={i}>
            <Skeleton variant="rounded" height={90} />
          </Grid>
        ))}
      </Grid>
      <Skeleton variant="rounded" height={80} />
      <Grid container spacing={2}>
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <Grid item xs={6} sm={4} md={3} key={i}>
            <Skeleton variant="rounded" height={90} />
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
interface Props {
  stats: IFinanceStats | null;
  vatSummary: IVatSummary | null;
  unclassifiedCount: number;
  categories: ITransactionCategory[];
  onGoToUnclassified: () => void;
  onGoToReconciliation: () => void;
}

export default function FinanceDashboard({
  stats,
  vatSummary,
  unclassifiedCount,
  categories,
  onGoToUnclassified,
  onGoToReconciliation,
}: Props) {
  if (!stats) return <DashboardSkeleton />;

  // Split category summaries by type
  const income =
    stats.categorySummaries?.filter((s) => s.categoryType === 'income') ?? [];
  const expenditure =
    stats.categorySummaries?.filter((s) => s.categoryType === 'expenditure') ??
    [];
  const finance =
    stats.categorySummaries?.filter((s) => s.categoryType === 'finance') ?? [];

  return (
    <Stack spacing={2.5}>
      {/* ── Row 1: Top KPI cards ── */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
          <KpiCard
            label="Total income"
            value={fCurrency(stats.totalIncome)}
            sub={stats.periodLabel}
            icon="eva:trending-up-fill"
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
          <KpiCard
            label="Total expenditure"
            value={fCurrency(stats.totalExpenditure)}
            sub={stats.periodLabel}
            icon="eva:trending-down-fill"
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
          <KpiCard
            label="Net profit"
            value={fCurrency(stats.netProfit)}
            sub={stats.netProfit >= 0 ? 'Profitable' : 'Loss'}
            icon="eva:bar-chart-2-fill"
            color={stats.netProfit >= 0 ? 'success' : 'error'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
          <KpiCard
            label="VAT recoverable"
            value={fCurrency(
              vatSummary ? vatSummary.recoverableVat : stats.vatRecoverable,
            )}
            sub={`${((vatSummary ? vatSummary.peRecoveryRate : stats.vatPeRate) * 100).toFixed(1)}% PE rate`}
            icon="eva:file-text-fill"
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
          <KpiCard
            label="Unclassified"
            value={String(unclassifiedCount)}
            sub={
              unclassifiedCount > 0 ? 'Click to review' : 'All categorised ✓'
            }
            icon="eva:alert-circle-fill"
            color={unclassifiedCount > 0 ? 'warning' : 'success'}
            onClick={unclassifiedCount > 0 ? onGoToUnclassified : undefined}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
          <KpiCard
            label="Total transactions"
            value={String(stats.totalTransactions)}
            sub={`${stats.reconciledCount} reconciled`}
            icon="eva:credit-card-fill"
            color="info"
          />
        </Grid>
      </Grid>

      {/* ── Row 2: Reconciliation bar + VAT snapshot ── */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <ReconciliationBar stats={stats} onGo={onGoToReconciliation} />
        </Grid>
        <Grid item xs={12} md={4}>
          <VatSnapshot stats={stats} vatSummary={vatSummary} />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <BusinessInsights stats={stats} />
        </Grid>

        <Grid item xs={12} md={6}>
          <FinancialTrendComparison stats={stats} />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <BusinessHealthSummary stats={stats} />
        </Grid>
      </Grid>
      <DashboardSummary stats={stats} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FinanceCharts stats={stats} />
        </Grid>
      </Grid>
      {/* ── Row 3: Category summary tiles ── */}
      {/* INCOME */}
      {income.length > 0 && (
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: 'success.main',
              }}
            />
            <Typography variant="subtitle2" color="success.dark">
              Income — {income.length} categor
              {income.length === 1 ? 'y' : 'ies'} ·{' '}
              <span style={{ fontFamily: 'monospace' }}>
                {fCurrency(income.reduce((s, c) => s + c.totalAmount, 0))} total
              </span>
            </Typography>
          </Stack>
          <Grid container spacing={1.5}>
            {income.map((s) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={s.categoryId}>
                <CategoryTile summary={s} />
              </Grid>
            ))}
          </Grid>
        </Stack>
      )}

      {/* EXPENDITURE */}
      {expenditure.length > 0 && (
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: 'error.main',
              }}
            />
            <Typography variant="subtitle2" color="error.dark">
              Expenditure — {expenditure.length} categor
              {expenditure.length === 1 ? 'y' : 'ies'} ·{' '}
              <span style={{ fontFamily: 'monospace' }}>
                {fCurrency(expenditure.reduce((s, c) => s + c.totalAmount, 0))}{' '}
                total
              </span>
            </Typography>
          </Stack>
          <Grid container spacing={1.5}>
            {expenditure.map((s) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={s.categoryId}>
                <CategoryTile summary={s} />
              </Grid>
            ))}
          </Grid>
        </Stack>
      )}

      {/* FINANCE ITEMS */}
      {finance.length > 0 && (
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: 'secondary.main',
              }}
            />
            <Typography variant="subtitle2" color="secondary.dark">
              Finance items — {finance.length} categor
              {finance.length === 1 ? 'y' : 'ies'} ·{' '}
              <span style={{ fontFamily: 'monospace' }}>
                {fCurrency(finance.reduce((s, c) => s + c.totalAmount, 0))}{' '}
                total
              </span>
            </Typography>
          </Stack>
          <Grid container spacing={1.5}>
            {finance.map((s) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={s.categoryId}>
                <CategoryTile summary={s} />
              </Grid>
            ))}
          </Grid>
        </Stack>
      )}

      {/* Empty state */}
      {income.length === 0 &&
        expenditure.length === 0 &&
        finance.length === 0 && (
          <Card sx={{ py: 6, textAlign: 'center' }}>
            <Iconify
              icon="eva:inbox-fill"
              width={44}
              sx={{
                color: 'text.disabled',
                display: 'block',
                mx: 'auto',
                mb: 1.5,
              }}
            />
            <Typography variant="subtitle1" color="text.secondary">
              No categorised transactions yet
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
              Import a bank statement and the dashboard will populate
              automatically.
            </Typography>
          </Card>
        )}
    </Stack>
  );
}
